/**
 * First-touch attribution capture.
 *
 * On first page load we record UTM params, referrer, and landing page into
 * localStorage. The payload rides along with OTP verification so the server
 * can stamp it onto the profile row (first-touch — never overwritten).
 */

const STORAGE_KEY = 'first_touch_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const CLICK_ID_KEYS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'li_fat_id'] as const;

// UTM sources our own product surfaces stamp on internal links (WhatsApp alert
// deep links, blog CTAs). They describe navigation, not acquisition, and must
// never overwrite a stored acquisition payload at runtime-merge time.
const INTERNAL_UTM_SOURCES = new Set(['whatsapp', 'organic_blog']);

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  checkout_page?: string;
  initial_referrer?: string;
  first_seen_at?: string;
  captured_at?: string;
  fbclid?: string;
  _fbc?: string;
  _fbp?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  ttclid?: string;
  li_fat_id?: string;
  source_surface?: string;
  lp_slug?: string;
  plan_id?: string;
  pricing_experiment?: string;
  pricing_variant?: string;
  litigant_price?: string;
  /** Referral code from /invite/<code> (Phase 3 Task 3.5). Set via storeReferralCode. */
  referral_code?: string;
}

const ATTRIBUTION_CONTEXT_KEYS = ['source_surface', 'lp_slug', 'plan_id', 'pricing_experiment', 'pricing_variant', 'litigant_price'] as const;

function sameOrigin(referrer: string): boolean {
  try {
    return new URL(referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]).slice(0, 500) : undefined;
}

function readRuntimeAttribution(params: URLSearchParams): Attribution {
  const payload: Attribution = {};

  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) payload[key] = v.slice(0, 200);
  }

  for (const key of CLICK_ID_KEYS) {
    const v = params.get(key);
    if (v) payload[key] = v.slice(0, 500);
  }

  for (const key of ATTRIBUTION_CONTEXT_KEYS) {
    const v = params.get(key);
    if (v) payload[key] = v.slice(0, 200);
  }

  const fbc = readCookie('_fbc');
  const fbp = readCookie('_fbp');
  if (fbc) payload._fbc = fbc;
  if (fbp) payload._fbp = fbp;

  return payload;
}

export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    const existingRaw = window.localStorage.getItem(STORAGE_KEY);
    if (existingRaw) {
      // Returning visitor clicking a fresh ad: without this, the click id is
      // lost forever (params drop on the first internal navigation) and the
      // signup can never be tied back to the paid click. Merge click ids +
      // the ad's own utm labels into the stored payload while preserving the
      // original first-touch landing page / referrer / first_seen_at.
      const params = new URLSearchParams(window.location.search);
      const hasFreshClickId = CLICK_ID_KEYS.some((key) => params.get(key));
      if (hasFreshClickId) {
        const existing = JSON.parse(existingRaw) as Attribution;
        const runtime = readRuntimeAttribution(params);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...runtime }));
      }
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const payload: Attribution = {};

    Object.assign(payload, readRuntimeAttribution(params));

    payload.landing_page = (window.location.pathname + window.location.search).slice(0, 500);

    const ref = document.referrer;
    if (ref && !sameOrigin(ref)) {
      payload.initial_referrer = ref.slice(0, 500);
    }

    payload.first_seen_at = new Date().toISOString();

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage can throw in private mode; attribution is best-effort
  }
}

// Runtime params win over the stored payload EXCEPT when they are our own
// internal navigation UTMs (e.g. the blog CTA's utm_source=organic_blog or a
// WhatsApp alert deep link) — those would relabel a paid/organic acquisition
// as internal traffic at checkout time.
function runtimeOverrides(params: URLSearchParams): Attribution {
  const runtime = readRuntimeAttribution(params);
  if (runtime.utm_source && INTERNAL_UTM_SOURCES.has(runtime.utm_source.toLowerCase())) {
    for (const key of UTM_KEYS) delete runtime[key];
  }
  return runtime;
}

export function getStoredAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return {
      ...(JSON.parse(raw) as Attribution),
      ...runtimeOverrides(new URLSearchParams(window.location.search)),
    };
  } catch {
    return null;
  }
}

export function getCheckoutAttribution(extra: Partial<Attribution> = {}): Attribution | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const stored = getStoredAttribution() ?? {};
  const runtime = runtimeOverrides(params);
  const currentPage = (window.location.pathname + window.location.search).slice(0, 1000);
  const payload: Attribution = {
    ...stored,
    ...runtime,
    checkout_page: currentPage,
    captured_at: new Date().toISOString(),
    ...extra,
  };

  if (!payload.landing_page) payload.landing_page = currentPage;
  if (!payload.first_seen_at) payload.first_seen_at = payload.captured_at;
  const ref = document.referrer;
  if (!payload.initial_referrer && ref && !sameOrigin(ref)) {
    payload.initial_referrer = ref.slice(0, 1000);
  }

  return Object.keys(payload).length > 0 ? payload : null;
}

export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Merge a referral code (from /invite/<code>) into the first-touch
 * attribution payload so it rides through to signup alongside UTM params.
 * Unlike captureFirstTouch(), this always writes — a referral visit should
 * win even if attribution was already captured earlier in the session.
 */
export function storeReferralCode(code: string): void {
  if (typeof window === 'undefined' || !code) return;
  try {
    const existingRaw = window.localStorage.getItem(STORAGE_KEY);
    const existing = existingRaw ? (JSON.parse(existingRaw) as Attribution) : {};
    const payload: Attribution = {
      ...existing,
      referral_code: code,
      first_seen_at: existing.first_seen_at || new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage can throw in private mode; attribution is best-effort
  }
}
